import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

import SimpleSelect from '@/components/atoms/SimpleSelect';
import TooltipIconText from '@/components/atoms/TooltipIconText';
import { TransportType } from '@/types/TransportType';
import { cn } from '@/utils/cn';
import PassengerInfoSection from './PassengerInfoSection';

export interface TransportDay {
	id: string;
	date: string;
	entryTime: string;
	exitTime: string;
	transportType: string | number | null;
	location: string | number | null;
	requestiaId?: string | null;
	requestiaStatus?: string | null;
}

interface TransportSectionProps {
	transportData: {
		days: TransportDay[];
		locations: Array<{ value: number; label: string }>;
		transportTypeOptions: Array<{ value: string; label: string }>;
		repeatLocation: { [key: string]: boolean };
	};
	isTransportExpanded: boolean;
	onTransportExpandedChange: (expanded: boolean) => void;
	onUpdateTransportDay: (
		id: string,
		field: keyof TransportDay,
		value: string | number | null,
	) => void;
	onRepeatLocationChange: (dayId: string, checked: boolean) => void;
	passengerInfo: {
		address: {
			cep: string;
			uf: string;
			city: string;
			neighborhood: string;
			street: string;
			number: string;
			complement: string;
		};
		phone: string;
		onPhoneChange: (value: string) => void;
		phoneRequired?: boolean;
	};
	isVacancyCover?: boolean;
	lockedTransportDate?: string;
}

interface RepeatToggleProps {
	repeatLocation: { [key: string]: boolean };
	day: TransportDay;
	onRepeatLocationChange: (dayId: string, checked: boolean) => void;
	transportDays: TransportDay[];
	onUpdateTransportDay: (
		id: string,
		field: keyof TransportDay,
		value: string | number | null,
	) => void;
	className?: string;
	isTransportTypeLocked?: (day: TransportDay) => boolean;
}

function RepeatToggle({
	repeatLocation,
	day,
	onRepeatLocationChange,
	transportDays,
	onUpdateTransportDay,
	className,
	isTransportTypeLocked,
}: RepeatToggleProps) {
	return (
		<div className={cn('w-full flex items-center gap-2 mb-1', className)}>
			<span className="text-[12px] text-[#505050] font-normal whitespace-nowrap">
				Repetir para próximos dias
			</span>
			<button
				type="button"
				role="switch"
				aria-checked={repeatLocation[day.id] || false}
				aria-label="Repetir informações para próximos dias"
				onClick={() => {
					const newValue = !repeatLocation[day.id];
					onRepeatLocationChange(day.id, newValue);

					if (newValue) {
						const sourceIndex = transportDays.findIndex(
							(item) => item.id === day.id,
						);
						if (sourceIndex < 0) {
							return;
						}
						const currentDay = transportDays[sourceIndex];
						transportDays.slice(sourceIndex + 1).forEach((nextDay) => {
							onUpdateTransportDay(
								nextDay.id,
								'entryTime',
								currentDay.entryTime,
							);
							onUpdateTransportDay(nextDay.id, 'exitTime', currentDay.exitTime);
							if (
								currentDay.transportType &&
								!isTransportTypeLocked?.(nextDay)
							) {
								onUpdateTransportDay(
									nextDay.id,
									'transportType',
									currentDay.transportType,
								);
							}
							if (currentDay.location) {
								onUpdateTransportDay(
									nextDay.id,
									'location',
									currentDay.location,
								);
							}
						});
					}
				}}
				className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
					repeatLocation[day.id] ? 'bg-primary' : 'bg-gray-300'
				}`}
			>
				<span
					className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
						repeatLocation[day.id] ? 'translate-x-6' : 'translate-x-1'
					}`}
				/>
			</button>
		</div>
	);
}

const TransportSection: React.FC<TransportSectionProps> = ({
	transportData,
	isTransportExpanded,
	onTransportExpandedChange,
	onUpdateTransportDay,
	onRepeatLocationChange,
	passengerInfo,
	isVacancyCover = false,
	lockedTransportDate,
}) => {
	const {
		days: transportDays,
		locations,
		transportTypeOptions,
		repeatLocation,
	} = transportData;
	const {
		address: passengerAddress,
		phone: passengerPhone,
		onPhoneChange: onPassengerPhoneChange,
		phoneRequired = false,
	} = passengerInfo;

	const isTransportTypeLocked = (day: TransportDay) =>
		Boolean(lockedTransportDate) && day.date === lockedTransportDate;
	const repeatSourceIndex = transportDays.findIndex(
		(transportDay) => !isTransportTypeLocked(transportDay),
	);

	return (
		<div className="mt-4 rounded-md border border-[#CBD5E1] p-4">
			<button
				type="button"
				onClick={() => onTransportExpandedChange(!isTransportExpanded)}
				className="flex w-full items-center justify-between mb-4"
			>
				<h3 className="text-lg font-semibold text-primary">Transporte</h3>
				{isTransportExpanded ? (
					<FaChevronUp className="text-primary" />
				) : (
					<FaChevronDown className="text-primary" />
				)}
			</button>

			{isTransportExpanded && (
				<>
					<div className="mb-6">
						<h4 className="mb-3 text-base font-semibold text-primary">
							Visualização das datas
						</h4>
						<div className="space-y-4">
							{transportDays.map((day, dayIndex) => {
								const [year, month, dayNum] = day.date.split('-');
								const formattedDate = `${dayNum}/${month}/${year}`;
								const isFirstDay = dayIndex === 0;
								const isRepeatSource = dayIndex === repeatSourceIndex;
								const isTransportTypeLockedForDay = isTransportTypeLocked(day);
								const isTransportNone =
									isTransportTypeLockedForDay ||
									day.transportType === ('None' as TransportType);

								const isBeingEdited = !!day.requestiaStatus;

								return (
									<div
										key={day.id}
										className="rounded-md border border-[#CBD5E1] bg-white p-3"
									>
										<div className="grid grid-cols-4 gap-4 items-end mb-3">
											<div className="w-full">
												<div className="mb-1 text-[12px] text-[#505050] font-normal">
													{isVacancyCover ? 'Data' : 'Data da ida extra'}
												</div>
												<input
													type="text"
													value={formattedDate}
													readOnly
													disabled={isTransportNone}
													className={`w-full h-8 rounded-[4px] border pl-2 text-xs border-gray-400 bg-gray-100 text-gray-600`}
												/>
											</div>
											<div className="w-full">
												<div className="mb-1 text-[12px] text-[#505050] font-normal">
													Horário de entrada
													{!isTransportNone && (
														<span className="text-red">*</span>
													)}
												</div>
												<input
													type="time"
													value={day.entryTime}
													disabled={isTransportNone}
													onChange={(e) => {
														const newValue = e.target.value;
														onUpdateTransportDay(day.id, 'entryTime', newValue);

														if (isRepeatSource && repeatLocation[day.id]) {
															transportDays
																.slice(dayIndex + 1)
																.forEach((nextDay) => {
																	onUpdateTransportDay(
																		nextDay.id,
																		'entryTime',
																		newValue,
																	);
																});
														}
													}}
													className={`w-full h-8 rounded-[4px] border pl-2 text-xs ${
														isTransportNone
															? 'border-gray-400 bg-gray-100 text-gray-600'
															: 'border-[#C4C4C4] text-[#212121]'
													}`}
												/>
											</div>
											<div className="w-full">
												<div className="mb-1 text-[12px] text-[#505050] font-normal">
													Horário de saída
													{!isTransportNone && (
														<span className="text-red">*</span>
													)}
												</div>
												<input
													type="time"
													value={day.exitTime}
													disabled={isTransportNone}
													onChange={(e) => {
														const newValue = e.target.value;
														onUpdateTransportDay(day.id, 'exitTime', newValue);
														if (isRepeatSource && repeatLocation[day.id]) {
															transportDays
																.slice(dayIndex + 1)
																.forEach((nextDay) => {
																	onUpdateTransportDay(
																		nextDay.id,
																		'exitTime',
																		newValue,
																	);
																});
														}
													}}
													className={`w-full h-8 rounded-[4px] border pl-2 text-xs ${
														isTransportNone
															? 'border-gray-400 bg-gray-100 text-gray-600'
															: 'border-[#C4C4C4] text-[#212121]'
													}`}
												/>
											</div>
											<div className="flex w-full items-end gap-1">
												<div className="min-w-0 flex-1">
													<SimpleSelect
														title="Tipo de transporte"
														values={transportTypeOptions}
														value={
															isTransportTypeLockedForDay
																? 'None'
																: day.transportType
														}
														disabled={isTransportTypeLockedForDay}
														setState={(value) => {
															if (isTransportTypeLockedForDay) {
																return;
															}
															onUpdateTransportDay(
																day.id,
																'transportType',
																value,
															);
															if (isRepeatSource && repeatLocation[day.id]) {
																transportDays
																	.slice(dayIndex + 1)
																	.forEach((nextDay) => {
																		if (isTransportTypeLocked(nextDay)) {
																			return;
																		}
																		onUpdateTransportDay(
																			nextDay.id,
																			'transportType',
																			value,
																		);
																	});
															}
														}}
														width="100%"
														fontSize="12px"
														titleColor="text-[#505050]"
														fontWeight="font-normal"
														selectSx={
															isTransportTypeLockedForDay
																? {
																		borderColor: 'var(--color-gray-400)',
																		backgroundColor: 'var(--color-gray-100)',
																		color: 'var(--color-gray-600)',
																	}
																: undefined
														}
														required
													/>
												</div>
												{isTransportTypeLockedForDay && (
													<TooltipIconText text="No dia de hoje, não é possível solicitar transporte. Os demais dias do período podem ser solicitados." />
												)}
											</div>
										</div>
										<div
											className={`grid gap-4 items-end ${isFirstDay ? 'grid-cols-2' : 'grid-cols-2'}`}
										>
											<div className="w-full">
												<SimpleSelect
													title="Local"
													values={locations}
													value={day.location}
													setState={(value) => {
														onUpdateTransportDay(day.id, 'location', value);
														if (isRepeatSource && repeatLocation[day.id]) {
															transportDays
																.slice(dayIndex + 1)
																.forEach((nextDay) => {
																	onUpdateTransportDay(
																		nextDay.id,
																		'location',
																		value,
																	);
																});
														}
													}}
													width="100%"
													fontSize="12px"
													required={!isTransportNone}
													disabled={isTransportNone}
													backgroundColor={
														isTransportNone
															? 'var(--color-gray-100)'
															: undefined
													}
													titleColor="text-[#505050]"
													fontWeight="font-normal"
												/>
											</div>
											{isBeingEdited && (
												<div className="flex gap-4 items-end">
													<div className="w-[50%]">
														<div className="mb-1 text-[12px] text-[#505050] font-normal">
															Status do Chamado de Transporte
														</div>
														<input
															type="text"
															value={day.requestiaStatus || ''}
															disabled={true}
															className="w-full h-8 rounded-[4px] border pl-2 text-xs border-gray-400 bg-gray-100 text-gray-600"
														/>
													</div>
													<div className="w-[50%]">
														<div className="mb-1 text-[12px] text-[#505050] font-normal">
															Código do Requestia
														</div>
														<input
															type="text"
															value={day.requestiaId || ''}
															disabled={true}
															className="w-full h-8 rounded-[4px] border pl-2 text-xs border-gray-400 bg-gray-100 text-gray-600"
														/>
													</div>
												</div>
											)}
											{isRepeatSource && !isBeingEdited && (
												<RepeatToggle
													repeatLocation={repeatLocation}
													day={day}
													onRepeatLocationChange={onRepeatLocationChange}
													transportDays={transportDays}
													onUpdateTransportDay={onUpdateTransportDay}
													isTransportTypeLocked={isTransportTypeLocked}
												/>
											)}
										</div>
										{isRepeatSource && isBeingEdited && (
											<RepeatToggle
												className="mt-3"
												repeatLocation={repeatLocation}
												day={day}
												onRepeatLocationChange={onRepeatLocationChange}
												transportDays={transportDays}
												onUpdateTransportDay={onUpdateTransportDay}
												isTransportTypeLocked={isTransportTypeLocked}
											/>
										)}
									</div>
								);
							})}
						</div>
					</div>

					<PassengerInfoSection
						address={passengerAddress}
						passengerPhone={passengerPhone}
						onPassengerPhoneChange={onPassengerPhoneChange}
						phoneRequired={phoneRequired}
					/>
				</>
			)}
		</div>
	);
};

export default TransportSection;
